/**
 * Filter/menu chrome: a DataButton that opens a Popover on click.
 * Positioning and dismiss live in Popover. For a short hint, use Tooltip.
 */
import { Fragment, isValidElement, ReactElement, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { DataButton, Icon, IconType } from "@sellar-io/components";
import { Placement } from "@floating-ui/react";
import { Popover } from "../popover/Popover";
import { PopoverContent } from "../popover/PopoverContent";
import { PopoverTrigger } from "../popover/PopoverTrigger";

export const FilterPopover = ({
  buttonText,
  icon,
  iconSize = 0.7,
  compact,
  children,
  className = "",
  active,
  toggleActive,
  clickOutside = true,
  placement = "bottom-start",
  border = true,
  contentClassName,
  buttonClassName,
  disableFlip = false,
  portal = false,
}: {
  buttonText: string;
  icon: IconType;
  iconSize?: number | string;
  compact: boolean;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  active: boolean;
  toggleActive: (value: boolean) => void;
  clickOutside?: boolean;
  placement?: Placement;
  border?: boolean;
  buttonClassName?: string;
  disableFlip?: boolean;
  portal?: boolean;
}): ReactElement | null => {
  if (!children) return null;

  if (Array.isArray(children) && !children.filter(Boolean).length) return null;

  if (
    isValidElement<{ children?: ReactNode }>(children) &&
    children.type === Fragment &&
    Array.isArray(children.props.children) &&
    !children.props.children.filter(Boolean).length
  )
    return null;

  return (
    <Popover
      trigger="click"
      open={active}
      onOpenChange={toggleActive}
      placement={placement}
      dismissOnOutsidePress={clickOutside}
      disableFlip={disableFlip}
    >
      <div className={className} style={{ width: "fit-content" }}>
        <PopoverTrigger>
          <DataButton
            onClick={() => toggleActive(!active)}
            border={border}
            active={active}
            className={twMerge("min-h-[38.78px]", buttonClassName)}
          >
            <Icon icon={icon} size={iconSize} />
            {compact ? null : buttonText}
          </DataButton>
        </PopoverTrigger>
        <PopoverContent
          portal={portal}
          className={twMerge(
            "mt-2 min-w-[380px] rounded-lg shadow-md",
            contentClassName,
          )}
        >
          {children}
        </PopoverContent>
      </div>
    </Popover>
  );
};
